import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  LinearProgress,
  Alert,
  Collapse,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import { Visibility, Schedule, AttachMoney, CheckCircle, Cancel, CompareArrows, DescriptionOutlined } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { brand } from '../../theme/colors';
import { useNavigate } from 'react-router-dom';
import ContractViewer from '../contracts/ContractViewer';
import contractService from '../../services/contractService';
import { stripMarkdown } from '../documents/documentStyles';
import TrustLine from '../contractors/TrustLine';

/**
 * Client Bid Review Page
 * 
 * Page for clients to review and approve/reject bids from contractors
 */
const ClientBidReviewPage = () => {
  const { getApiClient, user } = useAuth();
  const navigate = useNavigate();
  
  const [tabValue, setTabValue] = useState(0);
  const [bids, setBids] = useState([]);
  const [verifierProposals, setVerifierProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  // Quotations contractors shared as chat attachments (AI-detected) — not
  // official, not acceptable/payable until the contractor re-sends properly.
  const [chatQuotes, setChatQuotes] = useState([]);
  const [expandedBoq, setExpandedBoq] = useState(null);
  const [requestingId, setRequestingId] = useState(null);

  // Formal contracts for accepted bids: { [bidId]: contracts[] }
  const [contractsByBid, setContractsByBid] = useState({});
  const [contractViewerOpen, setContractViewerOpen] = useState(false);
  const [activeContract, setActiveContract] = useState(null);
  const [activeContractBid, setActiveContractBid] = useState(null);

  useEffect(() => {
    loadBids();
  }, [tabValue]);

  // Lazily fetch contracts for accepted bids so the client sees when one is
  // waiting for their signature. Party-only endpoint; 403/404 tolerated.
  useEffect(() => {
    const acceptedBids = bids.filter((b) => b.status === 'accepted');
    acceptedBids.forEach((bid) => {
      loadContractsForBid(bid.id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bids]);

  const loadContractsForBid = async (bidId) => {
    try {
      const list = await contractService.listForBid(bidId);
      setContractsByBid((prev) => ({ ...prev, [bidId]: Array.isArray(list) ? list : [] }));
    } catch (error) {
      setContractsByBid((prev) => ({ ...prev, [bidId]: [] }));
    }
  };

  const openContractViewer = (contract, bid) => {
    setActiveContract(contract);
    setActiveContractBid(bid);
    setContractViewerOpen(true);
  };

  useEffect(() => {
    (async () => {
      try {
        const apiClient = getApiClient();
        const res = await apiClient.get('/bids/chat-detections/mine');
        setChatQuotes(res.data.data || []);
      } catch (e) {
        // non-critical section — fail silently
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const requestOfficial = async (detection) => {
    setRequestingId(detection.id);
    try {
      const apiClient = getApiClient();
      await apiClient.post(`/bids/chat-detections/${detection.id}/request-official`);
      setChatQuotes((prev) => prev.map((q) =>
        q.id === detection.id ? { ...q, requestedAt: new Date().toISOString() } : q
      ));
    } catch (e) {
      console.error('Request official quotation failed:', e);
    } finally {
      setRequestingId(null);
    }
  };

  const loadBids = async () => {
    setLoading(true);
    try {
      const apiClient = getApiClient();
      const statusMap = ['all', 'submitted', 'under_review', 'accepted', 'rejected'];
      const status = statusMap[tabValue];
      
      // Load contractor bids
      const endpoint = status === 'all' 
        ? '/bids/client/me' 
        : `/bids/client/me?status=${status}`;
      
      const response = await apiClient.get(endpoint);
      setBids(response.data.data || []);

      // Load verification bids
      const proposalParams = status === 'all' 
        ? { clientId: user.id }
        : { clientId: user.id, status };
      
      const proposalResponse = await apiClient.get('/client/verifier-proposals', {
        params: proposalParams
      });
      setVerifierProposals(proposalResponse.data.data || []);
    } catch (error) {
      console.error('Error loading bids:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'default',
      submitted: 'info',
      under_review: 'warning',
      accepted: 'success',
      rejected: 'error',
      changes_requested: 'warning',
    };
    return colors[status] || 'default';
  };

  const getStatusIcon = (status) => {
    const icons = {
      submitted: <Schedule />,
      under_review: <Schedule />,
      accepted: <CheckCircle />,
      rejected: <Cancel />,
    };
    return icons[status];
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Quotations for Review
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Review and approve quotations for construction work and verification services
        </Typography>
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="All Quotations" />
          <Tab label="Pending Review" />
          <Tab label="Under Review" />
          <Tab label="Accepted" />
          <Tab label="Rejected" />
        </Tabs>
      </Paper>

      {loading ? (
        <LinearProgress />
      ) : (bids.length === 0 && verifierProposals.length === 0 && (tabValue !== 0 || chatQuotes.length === 0)) ? (
        <Alert severity="info">
          No bids to review at this time.
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {/* Compare Bids Banner — show per project when 2+ bids exist */}
          {(() => {
            const projectBidCounts = {};
            bids.forEach(b => {
              const pid = b.project_id;
              // Only count bids that are comparable (submitted or under_review)
              if (pid && ['submitted', 'under_review'].includes(b.status)) {
                if (!projectBidCounts[pid]) projectBidCounts[pid] = { count: 0, name: b.project_name };
                projectBidCounts[pid].count++;
              }
            });
            return Object.entries(projectBidCounts)
              .filter(([, v]) => v.count >= 2)
              .map(([projectId, { count, name }]) => (
                <Grid item xs={12} key={`compare-${projectId}`}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      borderRadius: 2, border: `1px solid ${brand.primaryLight}40`,
                      background: `linear-gradient(135deg, ${brand.primary}08, ${brand.primaryLight}12)`
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <CompareArrows sx={{ color: brand.primary }} />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {name || 'Project'} — {count} bids received
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Compare side-by-side with scoring, cost breakdown, and scope analysis
                        </Typography>
                      </Box>
                    </Box>
                    <Button
                      variant="contained"
                      startIcon={<CompareArrows />}
                      onClick={() => navigate(`/projects/${projectId}/compare-bids`)}
                      sx={{ background: brand.gradient, fontWeight: 600, whiteSpace: 'nowrap',
                        '&:hover': { background: brand.gradientHover } }}
                    >
                      Compare Bids
                    </Button>
                  </Paper>
                </Grid>
              ));
          })()}

          {/* Contractor Bids */}
          {bids.map((bid) => (
            <Grid item xs={12} key={`bid-${bid.id}`}>
              <Card sx={{ cursor: 'pointer', '&:hover': { boxShadow: 4 } }} onClick={() => navigate(`/bids/${bid.id}`)}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <Box sx={{ flexGrow: 1 }}>
                      {/* Bid Number and Status */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Chip label="CONTRACTOR QUOTATION" size="small" color="primary" variant="outlined" />
                        {bid.quote_source === 'uploaded' && (
                          <Chip label="OWN LETTERHEAD" size="small" color="secondary" variant="outlined" />
                        )}
                        <Typography variant="h6">{bid.bid_number}</Typography>
                        <Chip
                          label={bid.status.replace('_', ' ').toUpperCase()}
                          color={getStatusColor(bid.status)}
                          size="small"
                          icon={getStatusIcon(bid.status)}
                        />
                      </Box>

                      {/* Project Name */}
                      <Typography variant="body1" gutterBottom>
                        {bid.project_name || 'Untitled Project'}
                      </Typography>

                      {/* Contractor */}
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Contractor: {bid.contractor_name}
                      </Typography>
                      <TrustLine facts={bid.contractor_trust} sx={{ mb: 1 }} />

                      {/* Key Details */}
                      <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} sm={4}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AttachMoney fontSize="small" color="action" />
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Total Cost
                              </Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {bid.currency} {parseFloat(bid.total_cost || 0).toLocaleString()}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Schedule fontSize="small" color="action" />
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Duration
                              </Typography>
                              <Typography variant="body2">
                                {bid.estimated_duration ? `${bid.estimated_duration} ${bid.duration_unit || 'weeks'}` : '—'}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              {bid.submitted_at ? 'Submitted' : 'Created'}
                            </Typography>
                            <Typography variant="body2">
                              {new Date(bid.submitted_at || bid.created_at).toLocaleDateString()}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>

                      {/* Executive Summary Preview */}
                      {bid.executive_summary && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mt: 2,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {stripMarkdown(bid.executive_summary)}
                        </Typography>
                      )}
                    </Box>

                    {/* Actions */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, ml: 2 }}>
                      <Button
                        variant="contained"
                        startIcon={<Visibility />}
                        onClick={(e) => { e.stopPropagation(); navigate(`/bids/${bid.id}`); }}
                        sx={{
                          background: brand.gradient,
                        }}
                      >
                        Review Quotation
                      </Button>
                      {(() => {
                        if (bid.status !== 'accepted') return null;
                        const contracts = contractsByBid[bid.id] || [];
                        const sent = contracts.find((c) => c.status === 'sent');
                        const executed = contracts.find((c) => c.status === 'executed');
                        if (sent) {
                          return (
                            <Button
                              variant="contained"
                              color="success"
                              startIcon={<DescriptionOutlined />}
                              onClick={(e) => { e.stopPropagation(); openContractViewer(sent, bid); }}
                              sx={{ whiteSpace: 'nowrap' }}
                            >
                              Contract awaiting your signature
                            </Button>
                          );
                        }
                        if (executed) {
                          return (
                            <Button
                              variant="outlined"
                              startIcon={<DescriptionOutlined />}
                              onClick={(e) => { e.stopPropagation(); openContractViewer(executed, bid); }}
                              sx={{ whiteSpace: 'nowrap' }}
                            >
                              View contract
                            </Button>
                          );
                        }
                        return null;
                      })()}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}

          {/* Verification Bids */}
          {verifierProposals.map((proposal) => (
            <Grid item xs={12} key={`proposal-${proposal.id}`}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <Box sx={{ flexGrow: 1 }}>
                      {/* Bid Type and Status */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Chip label="VERIFICATION BID" size="small" color="secondary" variant="outlined" />
                        <Typography variant="h6">{proposal.request_id}</Typography>
                        <Chip
                          label={proposal.status.replace('_', ' ').toUpperCase()}
                          color={getStatusColor(proposal.status)}
                          size="small"
                          icon={getStatusIcon(proposal.status)}
                        />
                      </Box>

                      {/* Request Title */}
                      <Typography variant="body1" gutterBottom>
                        {proposal.request_title || proposal.verification_type || 'Verification Request'}
                      </Typography>

                      {/* Verifier */}
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Verifier: {proposal.verifier_first_name} {proposal.verifier_last_name} ({proposal.verifier_tier})
                      </Typography>

                      {/* Key Details */}
                      <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} sm={4}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AttachMoney fontSize="small" color="action" />
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Quote
                              </Typography>
                              <Typography variant="body2" fontWeight="bold">
                                USD {parseFloat(proposal.quote_amount || 0).toLocaleString()}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Schedule fontSize="small" color="action" />
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Available
                              </Typography>
                              <Typography variant="body2">
                                {proposal.available_date ? new Date(proposal.available_date).toLocaleDateString() : '-'}
                                {proposal.proposed_time && ` at ${proposal.proposed_time}`}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Submitted
                            </Typography>
                            <Typography variant="body2">
                              {new Date(proposal.created_at).toLocaleDateString()}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>

                      {/* Cover Letter Preview */}
                      {proposal.cover_letter && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mt: 2,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {proposal.cover_letter}
                        </Typography>
                      )}
                    </Box>

                    {/* Actions */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, ml: 2 }}>
                      <Button
                        variant="contained"
                        startIcon={<Visibility />}
                        onClick={() => navigate(`/verification-requests/${proposal.request_id}/proposals`)}
                        sx={{
                          background: brand.gradient,
                        }}
                      >
                        Review Quotation
                      </Button>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}

          {/* Quotations received as chat attachments — not yet official */}
          {tabValue === 0 && chatQuotes.length > 0 && (
            <>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mt: 2 }}>
                  Received in chat
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Quotations contractors sent you as chat attachments. They can't be accepted or paid
                  through Eqaya until the contractor submits them officially — you can ask them to with one click.
                </Typography>
              </Grid>
              {chatQuotes.map((q) => (
                <Grid item xs={12} key={`chat-quote-${q.id}`}>
                  <Card variant="outlined" sx={{ borderStyle: 'dashed', bgcolor: 'grey.50' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                        <Box sx={{ flexGrow: 1, minWidth: 240 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                            <Chip label="RECEIVED IN CHAT" size="small" color="warning" variant="outlined" />
                            <Chip label="NOT YET OFFICIAL" size="small" variant="outlined" />
                          </Box>
                          <Typography variant="body1" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
                            {q.fileName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            From {q.contractorName} · {new Date(q.receivedAt).toLocaleDateString()}
                          </Typography>
                          {q.totalAmount != null && (
                            <Typography variant="h6" sx={{ mt: 0.5, fontWeight: 700 }}>
                              {q.currency === 'USD' || !q.currency ? '$' : `${q.currency} `}{Number(q.totalAmount).toLocaleString()}
                              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                as read by Eqaya — unconfirmed
                              </Typography>
                            </Typography>
                          )}
                          {(q.lineItems || []).length > 0 && (
                            <Button size="small" variant="text" sx={{ mt: 0.5, px: 0 }}
                              onClick={() => setExpandedBoq(expandedBoq === q.id ? null : q.id)}>
                              {expandedBoq === q.id ? 'Hide breakdown ▲' : `View breakdown (${q.lineItems.length} items) ▼`}
                            </Button>
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {q.requestedAt ? (
                            <Button variant="outlined" disabled startIcon={<CheckCircle />}>
                              Official Quotation Requested
                            </Button>
                          ) : (
                            <Button
                              variant="contained"
                              disabled={requestingId === q.id}
                              onClick={() => requestOfficial(q)}
                              sx={{ background: brand.gradient }}
                            >
                              Request Official Quotation
                            </Button>
                          )}
                          <Button
                            variant="text"
                            onClick={() => navigate(`/messages?conversation=${q.conversationId}`)}
                          >
                            View in Chat
                          </Button>
                        </Box>
                      </Box>

                      {/* Eqaya BOQ preview — AI-read line items, contractor-unconfirmed */}
                      <Collapse in={expandedBoq === q.id} unmountOnExit>
                        <Box sx={{ mt: 2, overflowX: 'auto', bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>Item</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>Qty</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Unit</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>Unit price</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>Amount</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {(q.lineItems || []).map((li, i) => (
                                <TableRow key={i}>
                                  <TableCell>{li.description || '—'}</TableCell>
                                  <TableCell align="right">{li.quantity ?? '—'}</TableCell>
                                  <TableCell>{li.unit || '—'}</TableCell>
                                  <TableCell align="right">{li.unitPrice != null ? Number(li.unitPrice).toLocaleString() : '—'}</TableCell>
                                  <TableCell align="right">{li.amount != null ? Number(li.amount).toLocaleString() : '—'}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', p: 1 }}>
                            Read automatically from the PDF by Eqaya — the contractor hasn't confirmed these
                            numbers. Request the official quotation to make it binding and payable through Eqaya.
                          </Typography>
                        </Box>
                      </Collapse>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </>
          )}
        </Grid>
      )}

      {/* Contract review / signing */}
      {contractViewerOpen && activeContract && (
        <ContractViewer
          open={contractViewerOpen}
          onClose={() => setContractViewerOpen(false)}
          contract={activeContract}
          bid={activeContractBid}
          user={user}
          onChanged={() => activeContractBid && loadContractsForBid(activeContractBid.id)}
        />
      )}
    </Container>
  );
};

export default ClientBidReviewPage;
